/**
 * The opening sequence: two title scenes before the documentary chapters begin.
 *
 * The index used to start on a grid of cards, which answers "which one?" before it has
 * given anyone a reason to care. This starts the way the subject does — a peninsula,
 * then five places on it — and only then offers the list.
 *
 * Layered rather than flat. Parallax needs two things moving at different rates, and a
 * single plate cannot be given depth after the fact, so each scene carries a far layer
 * and a near one and drives them from the same `--progress` the chapters use.
 */

import { codaFor, openingFor, type UiCopy } from "./i18n";
import { DEFAULT_REGION, type RegionId } from "./regions";
const duneDrift = "video/dune-drift.mp4";
const nightDrift = "video/night-drift.mp4";
const orbitTurn = "video/orbit-turn.mp4";
const riyadhRise = "video/riyadh-rise.mp4";

import duneRidge from "./ornament/dune-ridge.webp?url";
import nightSky from "./ornament/night-sky.webp?url";
import orbitArabia from "./ornament/orbit-arabia.webp?url";
import sandstoneField from "./ornament/sandstone-field.webp?url";
import { createSceneVideo, publicUrl, type SceneVideo } from "./scene-video";

/**
 * Split a heading into per-word spans so they can arrive in sequence.
 *
 * Words, not characters: a per-letter stagger on a heading this size reads as a slot
 * machine, and it destroys the shape of Arabic entirely, where letters join and cannot
 * be separated into independent boxes without breaking the word.
 */
export function splitWords(text: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  const words = text.split(/\s+/u).filter((word) => word.length > 0);
  words.forEach((word, index) => {
    const outer = document.createElement("span");
    outer.className = "word";
    const inner = document.createElement("span");
    inner.className = "word__inner";
    inner.textContent = word;
    // The delay lives on the element that moves, so the clip stays put while its
    // contents slide up into it.
    inner.style.setProperty("--word-index", String(index));
    outer.append(inner);
    fragment.append(outer);
    if (index < words.length - 1) fragment.append(document.createTextNode(" "));
  });
  return fragment;
}

function heading(text: string): HTMLElement {
  const element = document.createElement("h2");
  element.className = "scene__title";
  element.dataset.reveal = "";
  element.append(splitWords(text));
  return element;
}

/**
 * The moving plates behind a heritage's two opening scenes.
 *
 * A region without an entry opens on Saudi Arabia's dunes, which is correct while it is
 * the only region with clips and wrong the moment another one has its own — so adding a
 * heritage means adding its plates here, and forgetting to is visible rather than
 * silent: a Greek title over a dune is not a subtle mistake.
 *
 * Greek posters are published photographs rather than bundled ornaments, so they need
 * the same base-path resolution the clips get. Using the reference frame as the poster
 * is deliberate: it is the video's own first frame, so the still that shows while the
 * clip loads is the shot rather than a different picture.
 */
type Plate = {
  video: string;
  poster: string;
  /**
   * The clip's picture is square, sitting in black bars inside a 16:9 file.
   *
   * Veo takes the shape of its reference frame over the aspectRatio it is asked for, and
   * these two were seeded with 1440x1440 Instagram exports, so both came back as a
   * square film pillarboxed into 1920x1080 — 420 columns of black down each side that
   * `object-fit: cover` cannot crop, because the black is inside the picture.
   *
   * `scripts/generate_hero_video.py` now crops every reference to 16:9 before sending
   * it, so a regenerated clip will not need this. Delete the flag when these two are
   * redone; it exists so a known-bad asset does not put black bars on the hero.
   */
  pillarboxed?: true;
};

/**
 * How a title plate is composed into the frame, which is a property of the shot rather
 * than a house style.
 *
 * `foot` is the Saudi opening: a low ridge under open sky, held to the bottom of the
 * frame and masked into the black so the type reads on emptiness above it. `full` is a
 * shot that is the whole frame — a push on a temple that fills it — where holding it to
 * the bottom 55% would crop the subject out and leave the title on a black band.
 *
 * The distinction has to be declared, because the geometry used to be hardcoded to the
 * dune's and every later heritage inherited a layout composed for someone else's
 * landscape.
 */
type PlateFit = "foot" | "full";

type TitlePlate = Plate & { fit: PlateFit };

const REGION_PLATES: Partial<Record<RegionId, { title: TitlePlate; orbit: Plate }>> = {
  greece: {
    title: {
      video: "video/acropolis-drift.mp4",
      poster: publicUrl("collection/parthenon.webp"),
      fit: "full",
      pillarboxed: true,
    },
    orbit: {
      video: "video/attic-dusk.mp4",
      poster: publicUrl("collection/athens-sunset.webp"),
      pillarboxed: true,
    },
  },
};

function platesFor(region: RegionId): { title: TitlePlate; orbit: Plate } {
  return (
    REGION_PLATES[region] ?? {
      title: { video: duneDrift, poster: duneRidge, fit: "foot" },
      orbit: { video: orbitTurn, poster: orbitArabia },
    }
  );
}

function layer(url: string, kind: string): HTMLElement {
  const image = document.createElement("div");
  image.className = `scene__layer scene__layer--${kind}`;
  image.style.backgroundImage = `url(${url})`;
  image.setAttribute("aria-hidden", "true");
  return image;
}

function scene(name: string): HTMLElement {
  const section = document.createElement("section");
  section.className = `scene scene--${name}`;
  // Marked for the scroll driver, not as a chapter: these are not exhibitions, and
  // anything counting exhibitions by [data-chapter] would silently gain two.
  section.dataset.scene = "";
  return section;
}

function stage(): HTMLElement {
  const element = document.createElement("div");
  element.className = "chapter__stage scene__stage";
  return element;
}

function body(): HTMLElement {
  const element = document.createElement("div");
  element.className = "scene__body";
  return element;
}

/** The title card: type on black, nothing else competing with it. */
function titleScene(copy: UiCopy, region: RegionId): HTMLElement {
  const section = scene("title");
  const words = openingFor(copy, region);
  const plate = platesFor(region).title;
  const holder = stage();
  const content = body();

  // No eyebrow here. storyEyebrow is "Saudi Arabian heritage" and so is the title, so
  // it printed the same words twice, one above the other.
  const lede = document.createElement("p");
  lede.className = "scene__lede";
  lede.dataset.reveal = "";
  lede.textContent = words.lede;

  const cue = document.createElement("p");
  cue.className = "scene__cue";
  cue.dataset.reveal = "";
  cue.setAttribute("aria-hidden", "true");
  cue.textContent = copy.storyScrollCue;

  content.append(heading(words.title), lede, cue);
  // The plate drifts up against the type, which is what keeps a title card from being a
  // flat colour field. Where it sits is the shot's own business — see `PlateFit`.
  holder.append(movingLayer(plate.video, plate.poster, plate.fit, plate), content);
  section.append(holder);
  return section;
}

/** The orbital view. The one invented image on the site that depicts a real place. */
function orbitScene(copy: UiCopy, region: RegionId): HTMLElement {
  const section = scene("orbit");
  const words = openingFor(copy, region);
  const plate = platesFor(region).orbit;
  const holder = stage();
  const content = body();

  const lede = document.createElement("p");
  lede.className = "scene__lede";
  lede.dataset.reveal = "";
  lede.textContent = words.orbitLede;

  content.append(heading(words.orbitTitle), lede);
  holder.append(movingLayer(plate.video, plate.poster, "orbit", plate), content);
  section.append(holder);
  return section;
}

/**
 * A breath between chapters.
 *
 * Five full-strength scenes in a row flatten each other out — everything is the loudest
 * thing on the page, so nothing is. An interlude is deliberately quieter and shorter
 * than a chapter: one line, one image, no way in and nothing to decide.
 */
export function createInterlude(kind: "sky" | "stone", copy: UiCopy): HTMLElement {
  const section = scene(`interlude interlude--${kind}`);
  section.classList.add("interlude");
  const holder = stage();
  const content = body();

  const line = document.createElement("p");
  line.className = "interlude__line";
  line.dataset.reveal = "";
  line.append(splitWords(kind === "sky" ? copy.interludeSky : copy.interludeStone));

  content.append(line);
  // The sky moves; the stone does not, and a time-lapse of a rock face would be an
  // effect rather than a subject.
  const plate =
    kind === "sky"
      ? movingLayer(nightDrift, nightSky, kind)
      : layer(sandstoneField, kind);
  holder.append(plate, content);
  section.append(holder);
  return section;
}

// Created inside the scenes, disposed with the index, so the caller does not have to
// thread a handle back out of every builder.
const clips: SceneVideo[] = [];

function movingLayer(
  source: string,
  poster: string,
  kind: string,
  plate?: Plate,
): HTMLElement {
  const clip = createSceneVideo(source, poster, kind);
  if (plate?.pillarboxed === true) clip.element.classList.add("scene__layer--boxed");
  clips.push(clip);
  return clip.element;
}

export function disposeOpening(): void {
  for (const clip of clips.splice(0)) clip.dispose();
}

/**
 * The closing scene: the five sites, and then the country that grew around them.
 *
 * Not an interlude. An interlude is a breath between two chapters and must never fall
 * last; this is deliberately last, and it is the only scene whose job is to look
 * forward rather than back.
 */
export function createCoda(copy: UiCopy, region: RegionId = DEFAULT_REGION): HTMLElement | null {
  const own = codaFor(copy, region);
  if (own !== null) return regionCoda(own);
  if (region !== DEFAULT_REGION) return null;

  const section = scene("coda");
  const holder = stage();
  const content = body();

  const line = document.createElement("p");
  line.className = "interlude__line";
  line.dataset.reveal = "";
  line.append(splitWords(copy.codaLine));

  content.append(line);
  // No still behind this one: the whole point of the shot is the change across it, and
  // any single frame is either the desert or the city, never the passage between them.
  holder.append(movingLayer(riyadhRise, nightSky, "coda"), content);
  section.append(holder);
  return section;
}

/**
 * A heritage's own closing plate.
 *
 * Carries a note as well as a line, because this is the one shot on the site that is
 * not downstream of a photograph. The whole page argues that a generation is not a
 * record; ending it on an unlabelled generation would give that away in the last frame.
 */
function regionCoda(words: { line: string; note: string }): HTMLElement {
  const section = scene("coda");
  const holder = stage();
  const content = body();

  const line = document.createElement("p");
  line.className = "interlude__line";
  line.dataset.reveal = "";
  line.append(splitWords(words.line));

  const note = document.createElement("p");
  note.className = "interlude__note";
  note.dataset.reveal = "";
  note.textContent = words.note;

  content.append(line, note);
  holder.append(
    movingLayer("video/attic-then-now.mp4", publicUrl("collection/parthenon.webp"), "full"),
    content,
  );
  section.append(holder);
  return section;
}

export function createOpening(copy: UiCopy, region: RegionId = DEFAULT_REGION): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.append(titleScene(copy, region), orbitScene(copy, region));
  return fragment;
}
